/**
 * OverviewCards — 4 financial summary cards at the top of the dashboard.
 *
 * Derives stats from the customer's service request list:
 *   • Active Requests   — ongoing SRs
 *   • Completed         — completed SRs
 *   • Money Saved       — coming soon
 *   • Net Worth         — coming soon
 */
import * as React from 'react';
import { CheckCircle2, PiggyBank, Wallet, Activity } from 'lucide-react';
import { Skeleton } from '@finmin/ui';
import { formatCurrency } from '@finmin/utils';
import type { CustomerServiceRequestResponse } from '@/api/service-requests';
import { isCompleted, isOngoing } from '../constants';

const FOREST = 'hsl(var(--palette-forest, 176 72% 17%))';
const CREAM  = 'hsl(var(--palette-cream,   40 100% 98%))';
const MINT   = 'hsl(var(--palette-mint,   154  38% 94%))';
/* Cream at ~70% alpha — used for muted text/icons on the Forest card.
 * Note: do NOT do `${CREAM}99` — that produces `hsl(...))99` (invalid CSS)
 * and the browser silently drops the rule, leaving the icon invisible. */
const CREAM_70 = 'hsl(var(--palette-cream, 40 100% 98%) / 0.70)';

// ── Types ─────────────────────────────────────────────────────────────────────

interface Props {
  requests: CustomerServiceRequestResponse[];
  isLoading: boolean;
  /** Customer-wide total saved (INR string from the profile). */
  totalMoneySaved?: string | null;
}

// ── Card definition ───────────────────────────────────────────────────────────

interface CardDef {
  label: string;
  value: React.ReactNode;
  icon: React.ElementType;
  bg: string;
  iconColor: string;
  valueColor: string;
  /** Background of the small icon-pill in the corner. Auto-derived when omitted. */
  iconPillBg?: string;
}

// ── Component ─────────────────────────────────────────────────────────────────

export function OverviewCards({ requests, isLoading, totalMoneySaved }: Props) {
  const activeCount    = requests.filter(isOngoing).length;
  const completedCount = requests.filter(isCompleted).length;
  // A positive total (anything other than null/"0.00") means the customer has
  // realised savings — show the figure; otherwise keep the "coming soon" hint
  // so an empty "₹0.00" doesn't read as a finished feature with nothing in it.
  const hasSavings =
    totalMoneySaved != null && Number(totalMoneySaved) > 0;

  const cards: CardDef[] = [
    {
      label:      'Active Requests',
      value:      activeCount,
      icon:       Activity,
      bg:         FOREST,
      iconColor:  CREAM_70,
      valueColor: CREAM,
      // Light pill on the dark Forest surface so the icon doesn't disappear.
      iconPillBg: 'hsl(var(--palette-cream, 40 100% 98%) / 0.14)',
    },
    {
      label:      'Completed Services',
      value:      completedCount,
      icon:       CheckCircle2,
      bg:         MINT,
      iconColor:  FOREST,
      valueColor: FOREST,
    },
    {
      label:      'Total Money Saved',
      value:      hasSavings ? formatCurrency(totalMoneySaved) : <ComingSoon />,
      icon:       PiggyBank,
      bg:         'hsl(var(--palette-butter, 43 100% 91%))',
      iconColor:  FOREST,
      valueColor: FOREST,
    },
    {
      label:      'Net Worth Tracked',
      value:      <ComingSoon />,
      icon:       Wallet,
      bg:         'hsl(var(--card))',
      iconColor:  'hsl(var(--muted-foreground))',
      valueColor: 'hsl(var(--foreground))',
    },
  ];

  return (
    <div className="grid grid-cols-2 lg:grid-cols-4 gap-3 sm:gap-4">
      {cards.map((card, idx) => (
        <OverviewCard key={card.label} card={card} isLoading={isLoading} index={idx} />
      ))}
    </div>
  );
}

// ── Single card ───────────────────────────────────────────────────────────────

function OverviewCard({
  card,
  isLoading,
  index,
}: {
  card: CardDef;
  isLoading: boolean;
  index: number;
}) {
  const Icon = card.icon;

  return (
    <div
      className={`rounded-2xl p-3 sm:p-4 flex flex-col gap-2 border border-transparent transition-all duration-200 hover:-translate-y-0.5 hover:shadow-md animate-slide-up-fade stagger-${Math.min(index + 1, 8)}`}
      style={{ backgroundColor: card.bg }}
    >
      <div className="flex items-center justify-between">
        <p
          className="text-[11px] font-semibold uppercase tracking-wide leading-snug"
          style={{ color: card.iconColor }}
        >
          {card.label}
        </p>
        <div
          className="h-7 w-7 rounded-lg flex items-center justify-center shrink-0"
          style={{
            backgroundColor:
              card.iconPillBg ??
              // Default: subtle dark tint that works on light surfaces.
              'hsl(var(--palette-forest, 176 72% 17%) / 0.08)',
          }}
        >
          <Icon className="h-3.5 w-3.5" style={{ color: card.iconColor }} aria-hidden />
        </div>
      </div>

      {isLoading ? (
        <Skeleton className="h-7 w-12 rounded" />
      ) : (
        <div
          className="text-2xl sm:text-3xl font-bold tracking-tight leading-none"
          style={{ color: card.valueColor }}
        >
          {card.value}
        </div>
      )}
    </div>
  );
}

function ComingSoon() {
  return (
    <span className="text-xs font-semibold uppercase tracking-widest opacity-40">
      Coming soon
    </span>
  );
}
